import { useCallback, useId, useMemo } from 'react';
import { MAX_SCRIPTURE_REFS } from '../../config/constants';
import type { TScripture } from '../../types';
import { BIBLE_BOOKS, findBibleBook, formatScriptureRef } from '../../utils';
import {
  addRef,
  REF_NUMBER_REDUCERS,
  refBounds,
  removeRef,
  selectRefBook,
  setFreeform,
} from './ScripturePicker.utils';
import type {
  IBookOption,
  IScripturePickerProps,
  IScriptureRowView,
  IUseScripturePickerReturn,
  TNumberCommit,
  TRefNumberField,
} from './types';

const BOOK_OPTIONS: IBookOption[] = BIBLE_BOOKS.map((book) => ({
  value: book.code,
  label: book.name,
}));

const PLACEHOLDER_ROW: IScriptureRowView = {
  index: 0,
  ref: null,
  bounds: null,
  label: 'New reference',
};

/** All logic for {@link ScripturePicker}: derives the rows and maps each control to one pure reducer. */
export function useScripturePicker({
  value,
  onChange,
  disabled = false,
}: IScripturePickerProps): IUseScripturePickerReturn {
  const descriptionId = useId();
  const rows = useMemo<IScriptureRowView[]>(
    () =>
      value.refs.length === 0
        ? [PLACEHOLDER_ROW]
        : value.refs.map((ref, index) => ({
            index,
            ref,
            bounds: refBounds(ref),
            label: formatScriptureRef(ref),
          })),
    [value.refs]
  );

  const emit = useCallback(
    (next: (current: TScripture) => TScripture) => {
      if (disabled) return;
      onChange(next(value));
    },
    [disabled, onChange, value]
  );

  const selectBook = useCallback(
    (index: number, book: string | null) => {
      const info = findBibleBook(book);
      if (!info) return;
      emit((current) => selectRefBook(current, index, info.code));
    },
    [emit]
  );

  const commitNumber = useCallback(
    (index: number, field: TRefNumberField, input: TNumberCommit) =>
      emit((current) => REF_NUMBER_REDUCERS[field](current, index, input)),
    [emit]
  );

  const hasRefs = value.refs.length > 0;

  return {
    rows,
    bookOptions: BOOK_OPTIONS,
    canAdd: hasRefs && value.refs.length < MAX_SCRIPTURE_REFS,
    descriptionId,
    selectBook,
    commitNumber,
    addRow: useCallback(() => emit(addRef), [emit]),
    removeRow: useCallback((index: number) => emit((current) => removeRef(current, index)), [emit]),
    changeFreeform: useCallback(
      (freeform: string) => emit((current) => setFreeform(current, freeform)),
      [emit]
    ),
  };
}
